import React, { useEffect, useMemo } from 'react';
import pathfinder from '@fiverr-private/pathfinder';
import { Container } from '@fiverr-private/layout_components';
import { useBreakpoint } from '@fiverr-private/hooks';
import { appTranslate } from '../../i18n';
import { useBackToFiverrUrl } from '../../hooks/useBackToFiverrUrl';
import { QueryClientProvider } from '../QueryClientProvider';
import { GoTab } from './GoSidebar.types';
import { GO_TABS_LIST } from './GoSidebar.constants';
import { GoDesktopBar } from './GoDesktopBar';
import { GoMobileDrawer } from './GoMobileDrawer';

export interface GoSidebarProps {
    drawerIsOpen?: boolean;
    onDrawerClose?: () => void;
    activeTab: GoTab;
    showHubTab?: boolean;
    showCreateTab?: boolean;
}

export const GoSidebar = ({
    activeTab,
    showHubTab = false,
    showCreateTab = false,
    drawerIsOpen = false,
    onDrawerClose,
}: GoSidebarProps) => {
    const breakpoint = useBreakpoint();
    const tabs = useMemo(
        () =>
            GO_TABS_LIST.filter((tab) => (tab.condition ? tab.condition({ showHubTab, showCreateTab }) : true)).map(
                ({ id, getHref, Icon }) => ({
                    label: appTranslate(`go_sidebar.tabs.${id}`),
                    Icon,
                    href: getHref({ source: activeTab }),
                    isActive: id === activeTab,
                })
            ),
        [activeTab, showHubTab, showCreateTab]
    );

    useEffect(() => {
        if (breakpoint !== 'default' && drawerIsOpen) {
            onDrawerClose?.();
        }
    }, [breakpoint, drawerIsOpen, onDrawerClose]);

    const backToFiverrUrl = useBackToFiverrUrl();
    const homeUrl = pathfinder(showHubTab ? 'seller_ai_hub_page' : 'go_explore_landing_page');

    return (
        <>
            <QueryClientProvider>
                <Container display={{ default: 'none', sm: 'initial' }}>
                    <GoDesktopBar tabs={tabs} homeUrl={homeUrl} backToFiverrUrl={backToFiverrUrl} />
                </Container>
                {breakpoint === 'default' && (
                    <GoMobileDrawer
                        tabs={tabs}
                        homeUrl={homeUrl}
                        backToFiverrUrl={backToFiverrUrl}
                        isOpen={drawerIsOpen}
                        onClose={onDrawerClose}
                    />
                )}
            </QueryClientProvider>
        </>
    );
};
